import React, { useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { SHOW_RESULTS_OPTIONS } from "@/data/dropdown-options";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/utils/cn";

export default function BasePagination({ data }) {
  const pagination = data?.meta ?? data;
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const onPageChange = (page) => {
    const params = new URLSearchParams(location.search);
    let currentPage = parseInt(params.get("page") || 1, 10);

    if (page) {
      const pageAction = page.toString().toLowerCase();

      if (pageAction.includes("previous")) {
        currentPage = Math.max(currentPage - 1, 1);
      } else if (pageAction.includes("next")) {
        currentPage += 1;
      } else {
        currentPage = parseInt(page, 10) || currentPage;
      }

      params.set("page", currentPage);
    } else {
      params.delete("page");
    }

    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const renderLinks = () => {
    if (!pagination?.links?.length) return null;

    return pagination.links.map((link, idx) => {
      const label = link.label.toString().toLowerCase();
      const isPrevious = label.includes("previous");
      const isNext = label.includes("next");

      return (
        <button
          key={idx}
          className={cn(
            "border font-light w-8 h-[28px] flex items-center justify-center transition-all duration-200",
            {
              "bg-black text-[#E1D05E]": link.active,
              "hover:bg-gray-100": !link.active,
              "disabled:bg-gray-100 cursor-not-allowed": !link.url,
            }
          )}
          disabled={!link.url}
          onClick={() => onPageChange(link.label)}
        >
          {isPrevious && <FaChevronLeft size={10} />}
          {!isPrevious && !isNext && link.label}
          {isNext && <FaChevronRight size={10} />}
        </button>
      );
    });
  };

  const onSelect = (query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set("page", 1);
      params.set("perPage", query);
    } else {
      params.delete("perPage");
    }
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  };
  const [dropdownPosition, setDropdownPosition] = useState("bottom");

  useEffect(() => {
    if (buttonRef.current) {
      const handleResize = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;

        if (spaceBelow > 300) {
          setDropdownPosition("bottom");
        } else {
          setDropdownPosition("top");
        }
      };

      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(buttonRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [setDropdownPosition]);

  return (
    <div className="w-full font-light bg-white flex items-center justify-between">
      <div className="flex items-center gap-x-2 text-sm">
        <span>Show Results</span>
        <div className="relative flex flex-col">
          <div
            ref={buttonRef}
            className={cn(
              "w-32 rounded-[7px] h-[28px] p-[1px] bg-[#9a9a9a] hover:bg-gradient-to-r from-[#2E6ABD] via-[#F84160] to-[#009900] relative",
              isOpen && "bg-gradient-to-r",
              isOpen
                ? dropdownPosition == "top"
                  ? "rounded-t-none"
                  : "rounded-b-none"
                : ""
            )}
          >
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "bg-white px-2 h-full rounded-[6px] w-full flex items-center",
                isOpen
                  ? dropdownPosition == "top"
                    ? "rounded-t-none"
                    : "rounded-b-none"
                  : ""
              )}
            >
              <span className="text-sm flex-1 text-left">
                {
                  SHOW_RESULTS_OPTIONS.find(
                    (v) => v.value === pagination?.per_page
                  )?.label
                }
              </span>
              <svg
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transition-transform duration-300 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              >
                <path
                  d="M1 1L7 7L13 1"
                  stroke="currentColor"
                  className={`${isOpen ? "stroke-[1px] " : "stroke-[0.7px] "}`}
                />
              </svg>
            </button>
          </div>
          <div
            ref={dropdownRef}
            className={cn(
              "w-full group z-50 p-[1px] absolute rounded-[7px] bg-gradient-to-r from-[#2E6ABD] via-[#F84160] to-[#009900] from-[5%] via-[50%] to-[100%] transition-all duration-300 ",
              isOpen
                ? "opacity-100 translate-y-0"
                : "hidden opacity-0 translate-y-[-20px]",
              dropdownPosition === "top"
                ? "bottom-[28px] pb-0  rounded-b-none"
                : "top-[28px] pt-0 rounded-t-none"
            )}
          >
            <div
              className={cn(
                "w-full h-full pt-[6px] px-[10px] rounded-[6.4px] flex flex-col bg-white max-h-[300px] overflow-y-auto",
                dropdownPosition === "top"
                  ? "rounded-t-[6.4px] rounded-b-none"
                  : "rounded-b-[6.4px] rounded-t-none"
              )}
            >
              <div
                className={cn(
                  "flex",
                  dropdownPosition === "top" ? "flex-col-reverse" : "flex-col"
                )}
              >
                {SHOW_RESULTS_OPTIONS?.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      onSelect(option.value);
                      setIsOpen(false);
                    }}
                    className="h-8 w-full cursor-pointer hover:bg-neutral-50"
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex cursor-pointer font-light text-sm">
        <button
          onClick={() => onPageChange(1)}
          className="border w-12 h-[28px] flex items-center justify-center hover:bg-gray-100 transition-all duration-200"
        >
          First
        </button>
        {renderLinks()}
        <button
          onClick={() => onPageChange(pagination?.last_page)}
          className="border w-12 h-[28px] flex items-center justify-center hover:bg-gray-100 transition-all duration-200"
        >
          Last
        </button>
      </div>
    </div>
  );
}
